import { supabase } from "@/integrations/supabase/client";

/**
 * Wrapper around supabase.functions.invoke that extracts meaningful error
 * messages instead of the generic "Edge Function returned a non-2xx status code."
 *
 * Usage:
 *   const { data, error } = await invokeFunction("send-portal-invite", {
 *     body: { client_id },
 *   });
 *   if (error) throw new Error(error);
 */
export async function invokeFunction<T = any>(
  functionName: string,
  options?: {
    body?: Record<string, any>;
    headers?: Record<string, string>;
  }
): Promise<{ data: T | null; error: string | null }> {
  try {
    const { data, error } = await supabase.functions.invoke(functionName, options);

    if (error) {
      // The Supabase client wraps non-2xx responses in a FunctionsHttpError
      // with a generic message. The actual error is in the response body.
      const errorMessage =
        (typeof data === "object" && data?.error) ||
        error.message?.replace("Edge Function returned a non-2xx status code", "Something went wrong") ||
        "Something went wrong. Please try again.";
      return { data: null, error: errorMessage };
    }

    // Some functions return { error: "..." } in the body with a 200 status
    if (typeof data === "object" && data?.error && !data?.success) {
      return { data: null, error: data.error };
    }

    return { data, error: null };
  } catch (err: any) {
    return {
      data: null,
      error: err.message || "Something went wrong. Please try again.",
    };
  }
}

/**
 * Same as invokeFunction but auto-attaches the current user's auth token.
 */
export async function invokeAuthenticatedFunction<T = any>(
  functionName: string,
  options?: {
    body?: Record<string, any>;
    headers?: Record<string, string>;
  }
): Promise<{ data: T | null; error: string | null }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return { data: null, error: "You're not logged in. Please sign in and try again." };
  }

  return invokeFunction<T>(functionName, {
    ...options,
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${session.access_token}`,
    },
  });
}
